import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import authService from "../services/discordAuth";
import ReactMarkdown from "react-markdown";
import { trackEvent } from "../utils/analytics";
import StatusModal from "../components/StatusModal";
import "./AddBotPage.css"; // Reutilizando os estilos
import "./TagsStyles.css"; // Estilos modernos para tags e categorias
import usePageTitle from "../hooks/usePageTitle";

function EditBotPage() {
  const { t } = useTranslation();
  usePageTitle(t("editBot.pageTitle"));

  const navigate = useNavigate();
  const { botId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dbCategories, setDbCategories] = useState([]);
  const [dbTags, setDbTags] = useState([]);

  // Paginação
  const [categoryPage, setCategoryPage] = useState(0);
  const [tagPage, setTagPage] = useState(0);
  const itemsPerPage = 20;

  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showLongDescPreview, setShowLongDescPreview] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({ type: "success", title: "", message: "" });

  const showStatus = (type, message, title) => {
    setModalConfig({ type, message, title });
    setModalOpen(true);
  };

  const [botData, setBotData] = useState({
    shortDescription: "",
    longDescription: "",
    prefix: "",
    supportServer: "",
    website: "",
    github: "",
    categories: [],
    tags: [],
    commands: [],
    reviewNotes: "",
    testInstructions: "",
    additionalInfo: "",
    apiTier: "free",
    vanityUrl: "",
    whiteLabelSettings: { palette: "midnight" },
  });

  const [botStatus, setBotStatus] = useState(null); // Armazena o status atual do bot

  const [errors, setErrors] = useState({});

  // Verifica autenticação
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate("/");
    }
  }, [navigate]);

  // Busca dados do bot e opções disponíveis
  useEffect(() => {
    const fetchData = async () => {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const user = authService.getUser();

      try {
        // Buscar categorias disponíveis
        const catRes = await fetch(`${BACKEND_URL}/api/bots/categories`);
        if (catRes.ok) {
          const catData = await catRes.json();
          setDbCategories(catData);
        }

        // Buscar tags disponíveis
        const tagRes = await fetch(`${BACKEND_URL}/api/bots/tags`);
        if (tagRes.ok) {
          const tagData = await tagRes.json();
          setDbTags(tagData);
        }

        // Buscar dados do bot
        const botRes = await fetch(`${BACKEND_URL}/api/bots/${botId}/edit?userId=${user.id}`);
        if (botRes.ok) {
          const data = await botRes.json();
          setBotData({
            shortDescription: data.bot.short_description || "",
            longDescription: data.bot.long_description || "",
            prefix: data.bot.prefix || "",
            supportServer: data.bot.support_server || "",
            website: data.bot.website || "",
            github: data.bot.github || "",
            twitter: data.bot.twitter || "",
            instagram: data.bot.instagram || "",
            youtube: data.bot.youtube || "",
            tagline: data.bot.tagline || "",
            categories: data.bot.categories || [],
            tags: data.bot.tags || [],
            commands: data.bot.commands || [],
            reviewNotes: data.bot.review_notes || "",
            testInstructions: data.bot.test_instructions || "",
            additionalInfo: data.bot.additional_info || "",
            apiTier: data.bot.api_tier || "free",
            vanityUrl: data.bot.vanity_url || "",
            ownerBadges: data.bot.owner_badges || [],
            whiteLabelSettings:
              typeof data.bot.white_label_settings === "string" ? JSON.parse(data.bot.white_label_settings) : data.bot.white_label_settings || { palette: "midnight" },
          });
          setBotStatus(data.bot.status); // Armazena o status atual
        } else {
          const errorData = await botRes.json();
          showStatus("error", errorData.error || t("editBot.errors.loadError"));
          navigate("/profile");
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        showStatus("error", t("editBot.errors.loadDataError"));
        navigate("/profile");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [botId, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBotData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Limpar erro do campo
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const isCustomizationEnabled = botData.apiTier === "business" || (botData.apiTier === "premium" && botData.ownerBadges?.includes("Apoiador Rincko"));

  const toggleCategory = (categoryId) => {
    setBotData((prev) => ({
      ...prev,
      categories: prev.categories.includes(categoryId) ? prev.categories.filter((id) => id !== categoryId) : [...prev.categories, categoryId],
    }));
  };

  const toggleTag = (tagId) => {
    setBotData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId) ? prev.tags.filter((id) => id !== tagId) : [...prev.tags, tagId],
    }));
  };

  const addCommand = () => {
    if (botData.commands.length < 10) {
      setBotData((prev) => ({
        ...prev,
        commands: [...prev.commands, { name: "", description: "", usage: "", category: "Geral" }],
      }));
    }
  };

  const removeCommand = (index) => {
    setBotData((prev) => ({
      ...prev,
      commands: prev.commands.filter((_, i) => i !== index),
    }));
  };

  const updateCommand = (index, field, value) => {
    const updatedCommands = [...botData.commands];
    updatedCommands[index][field] = value;
    setBotData((prev) => ({ ...prev, commands: updatedCommands }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!botData.shortDescription.trim()) {
        newErrors.shortDescription = t("editBot.errors.shortDescRequired");
      } else if (botData.shortDescription.length < 20) {
        newErrors.shortDescription = t("editBot.errors.shortDescMin");
      } else if (botData.shortDescription.length > 200) {
        newErrors.shortDescription = t("editBot.errors.shortDescMax");
      }

      if (!botData.longDescription.trim()) {
        newErrors.longDescription = t("editBot.errors.longDescRequired");
      } else if (botData.longDescription.length < 100) {
        newErrors.longDescription = t("editBot.errors.longDescMin");
      } else if (botData.longDescription.length > 2000) {
        newErrors.longDescription = t("editBot.errors.longDescMax");
      }

      if (!botData.prefix.trim()) {
        newErrors.prefix = t("editBot.errors.prefixRequired");
      }

      if (botData.categories.length === 0) {
        newErrors.categories = t("editBot.errors.categoryRequired");
      }

      if (botData.tags.length === 0) {
        newErrors.tags = t("editBot.errors.tagRequired");
      }
    }

    if (currentStep === 2) {
      if (botData.commands.length < 1) {
        newErrors.commands = t("editBot.errors.commandsMin");
      } else {
        botData.commands.forEach((cmd, index) => {
          if (!cmd.name.trim()) newErrors[`cmd_${index}_name`] = t("editBot.errors.cmdNameRequired");
          if (!cmd.description.trim()) newErrors[`cmd_${index}_desc`] = t("editBot.errors.cmdDescRequired");
          if (!cmd.usage.trim()) newErrors[`cmd_${index}_usage`] = t("editBot.errors.cmdUsageRequired");
        });
      }
    }

    // Validação da Etapa 3 (apenas para bots rejeitados)
    if (currentStep === 3 && botStatus === "rejected") {
      if (!botData.reviewNotes.trim()) {
        newErrors.reviewNotes = t("editBot.errors.notesRequired");
      } else if (botData.reviewNotes.length < 20) {
        newErrors.reviewNotes = t("editBot.errors.notesMin");
      }

      if (!botData.testInstructions.trim()) {
        newErrors.testInstructions = t("editBot.errors.testRequired");
      } else if (botData.testInstructions.length < 20) {
        newErrors.testInstructions = t("editBot.errors.testMin");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showStatus("warning", t("editBot.errors.formErrors"));
      return;
    }

    setSaving(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
    const user = authService.getUser();

    try {
      // Preparar payload
      const payload = {
        short_description: botData.shortDescription,
        long_description: botData.longDescription,
        tagline: botData.tagline,
        prefix: botData.prefix,
        support_server: botData.supportServer,
        website: botData.website,
        github: botData.github,
        twitter: botData.twitter,
        instagram: botData.instagram,
        youtube: botData.youtube,
        categories: botData.categories,
        tags: botData.tags,
        commands: botData.commands,
        vanity_url: botData.vanityUrl,
        white_label_settings: botData.whiteLabelSettings,
      };

      // Se o bot estava rejeitado, mudar status para reanálise e incluir campos de revisão
      if (botStatus === "rejected") {
        payload.status = "reanalysis";
        payload.review_notes = botData.reviewNotes;
        payload.test_instructions = botData.testInstructions;
        payload.additional_info = botData.additionalInfo;
      }

      const response = await fetch(`${BACKEND_URL}/api/bots/${botId}?userId=${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        trackEvent("bot_edit_success", { bot_id: botId });
        setShowSuccessModal(true);
      } else {
        const errorData = await response.json();
        trackEvent("bot_edit_error", { bot_id: botId, error: errorData.error });
        showStatus("error", errorData.error || t("editBot.errors.formErrors"));
      }
    } catch (error) {
      console.error("Erro ao atualizar bot:", error);
      trackEvent("bot_edit_error", { bot_id: botId, error: "catch_error" });
      showStatus("error", t("editBot.errors.formErrors"));
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (validateForm()) {
      const availableSteps = [1, 2];
      if (isCustomizationEnabled) {
        availableSteps.push(3);
      }
      if (botStatus === "rejected") availableSteps.push(4);

      const currentIndex = availableSteps.indexOf(currentStep);
      const nextStep = availableSteps[currentIndex + 1];

      if (nextStep) {
        trackEvent("bot_edit_next_step", {
          bot_id: botId,
          from_step: currentStep,
          to_step: nextStep,
        });
        setCurrentStep(nextStep);
        window.scrollTo(0, 0);
      }
    } else {
      showStatus("warning", t("editBot.errors.fillRequired"));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
    window.scrollTo(0, 0);
  };

  if (loading) {
    return (
      <div className="add-bot-page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{t("editBot.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-bot-page">
      <div className="container">
        <div className="add-bot-header">
          <h1>{t("editBot.header.title")}</h1>
          <p>
            {currentStep === 1
              ? t("editBot.header.step1")
              : currentStep === 2
                ? t("editBot.header.step2")
                : currentStep === 3
                  ? t("editBot.header.step3")
                  : t("editBot.header.step4")}
          </p>
        </div>

        <div className="add-bot-content">
          {/* Sidebar de Etapas */}
          <div className="steps-sidebar">
            <div className="steps-list">
              {[
                { num: 1, title: t("editBot.sidebar.step1"), icon: "📝" },
                { num: 2, title: t("editBot.sidebar.step2"), icon: "⚡" },
                ...(isCustomizationEnabled ? [{ num: 3, title: t("editBot.sidebar.step3"), icon: "🎨" }] : []),
                ...(botStatus === "rejected" ? [{ num: 4, title: t("editBot.sidebar.step4"), icon: "🔄" }] : []),
              ].map((step, idx, arr) => (
                <div
                  key={step.num}
                  className={`step-item ${currentStep === step.num ? "active" : ""} ${currentStep > step.num ? "completed" : ""}`}
                  onClick={() => {
                    if (step.num < currentStep || step.num === 1) {
                      setCurrentStep(step.num);
                    } else if (validateForm()) {
                      setCurrentStep(step.num);
                    }
                  }}
                >
                  <div className="step-number">{currentStep > step.num ? "✓" : step.icon}</div>
                  <div className="step-info">
                    <div className="step-title">{step.title}</div>
                    <div className="step-subtitle">{t("editBot.sidebar.label", { num: step.num })}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Formulário de Edição */}
          <div className="form-container">
            <form onSubmit={handleSubmit} className="add-bot-form">
              {currentStep === 1 && (
                <div className="form-step animation-fade-in">
                  <h2>{t("editBot.steps.basic.title")}</h2>
                  <p className="step-description">{t("editBot.steps.basic.description")}</p>

                  <div className="form-group">
                    <label htmlFor="shortDescription">
                      {t("editBot.steps.basic.shortDesc")}
                      <span className="char-count">{botData.shortDescription.length}/200</span>
                    </label>
                    <input
                      type="text"
                      id="shortDescription"
                      name="shortDescription"
                      value={botData.shortDescription}
                      onChange={handleInputChange}
                      maxLength={200}
                      placeholder={t("editBot.steps.basic.shortDescPlaceholder")}
                      className={errors.shortDescription ? "error" : ""}
                    />
                    {errors.shortDescription && <span className="error-message">{errors.shortDescription}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="tagline">
                      {t("editBot.steps.basic.tagline")}
                      <span className="char-count">{(botData.tagline || "").length}/150</span>
                    </label>
                    <input
                      type="text"
                      id="tagline"
                      name="tagline"
                      value={botData.tagline || ""}
                      onChange={handleInputChange}
                      maxLength={150}
                      placeholder={t("editBot.steps.basic.taglinePlaceholder")}
                    />
                  </div>

                  <div className="form-group">
                    <div className="label-with-action">
                      <label htmlFor="longDescription">
                        {t("editBot.steps.basic.longDesc")}
                        <span className="char-count">{botData.longDescription.length}/2000</span>
                      </label>
                      <button type="button" className="btn-preview-toggle" onClick={() => setShowLongDescPreview(!showLongDescPreview)}>
                        {showLongDescPreview ? "✍️ Editar" : "👁️ Preview"}
                      </button>
                    </div>

                    {showLongDescPreview ? (
                      <div className="markdown-preview-container markdown-body">
                        <ReactMarkdown>{botData.longDescription || "_Sem descrição para visualizar_"}</ReactMarkdown>
                      </div>
                    ) : (
                      <textarea
                        id="longDescription"
                        name="longDescription"
                        value={botData.longDescription}
                        onChange={handleInputChange}
                        rows={8}
                        maxLength={2000}
                        placeholder={t("editBot.steps.basic.longDescPlaceholder")}
                        className={errors.longDescription ? "error" : ""}
                      />
                    )}

                    {errors.longDescription && <span className="error-message">{errors.longDescription}</span>}
                    <small className="help-text">Suporta Markdown (Negrito, Itálico, Listas, etc)</small>
                  </div>

                  <div className="form-grid" style={{ marginBottom: "1.5rem" }}>
                    <div className="form-group">
                      <label htmlFor="prefix">{t("editBot.steps.basic.prefix")}</label>
                      <input
                        type="text"
                        id="prefix"
                        name="prefix"
                        value={botData.prefix}
                        onChange={handleInputChange}
                        placeholder={t("editBot.steps.basic.prefixPlaceholder")}
                        className={errors.prefix ? "error" : ""}
                      />
                      {errors.prefix && <span className="error-message">{errors.prefix}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="supportServer">{t("editBot.steps.basic.supportServer")}</label>
                      <input type="url" id="supportServer" name="supportServer" value={botData.supportServer} onChange={handleInputChange} placeholder="https://..." />
                    </div>
                  </div>

                  <div className="form-grid" style={{ marginBottom: "1.5rem" }}>
                    <div className="form-group">
                      <label htmlFor="website">{t("editBot.steps.basic.website")}</label>
                      <input type="url" id="website" name="website" value={botData.website} onChange={handleInputChange} placeholder="https://..." />
                    </div>
                    <div className="form-group">
                      <label htmlFor="github">{t("editBot.steps.basic.github")}</label>
                      <input type="url" id="github" name="github" value={botData.github} onChange={handleInputChange} placeholder="https://..." />
                    </div>
                  </div>

                  <div className="form-grid" style={{ marginBottom: "1.5rem" }}>
                    <div className="form-group">
                      <label htmlFor="twitter">{t("editBot.steps.basic.twitter")}</label>
                      <input type="url" id="twitter" name="twitter" value={botData.twitter || ""} onChange={handleInputChange} placeholder="https://twitter.com/..." />
                    </div>
                    <div className="form-group">
                      <label htmlFor="instagram">{t("editBot.steps.basic.instagram")}</label>
                      <input type="url" id="instagram" name="instagram" value={botData.instagram || ""} onChange={handleInputChange} placeholder="https://instagram.com/..." />
                    </div>
                  </div>

                  <div className="form-grid" style={{ marginBottom: "1.5rem" }}>
                    <div className="form-group">
                      <label htmlFor="youtube">{t("editBot.steps.basic.youtube")}</label>
                      <input type="url" id="youtube" name="youtube" value={botData.youtube || ""} onChange={handleInputChange} placeholder="https://youtube.com/..." />
                    </div>
                    <div className="form-group"></div>
                  </div>

                  <div className="form-group">
                    <label>
                      {t("editBot.steps.basic.categories", {
                        count: botData.categories.length,
                      })}
                    </label>
                    <div className="tags-pagination-container">
                      <div className="tags-grid">
                        {dbCategories && dbCategories.length > 0 ? (
                          dbCategories.slice(categoryPage * itemsPerPage, (categoryPage + 1) * itemsPerPage).map((category, index) => (
                            <button
                              key={category.id || index}
                              type="button"
                              className={`tag-button ${botData.categories.includes(category.id) ? "active" : ""}`}
                              onClick={() => toggleCategory(category.id)}
                            >
                              <span>{category.icon}</span> {category.name}
                              {botData.categories.includes(category.id) && <span className="check-badge">✓</span>}
                            </button>
                          ))
                        ) : (
                          <p>Carregando categorias...</p>
                        )}
                      </div>
                      {dbCategories.length > itemsPerPage && (
                        <div className="pagination-controls">
                          <button type="button" className="pagination-btn" onClick={() => setCategoryPage((prev) => Math.max(0, prev - 1))} disabled={categoryPage === 0}>
                            {t("editBot.steps.basic.pagination.prev")}
                          </button>
                          <span className="pagination-info">
                            {t("editBot.steps.basic.pagination.info", {
                              current: categoryPage + 1,
                              total: Math.ceil(dbCategories.length / itemsPerPage),
                            })}
                          </span>
                          <button
                            type="button"
                            className="pagination-btn"
                            onClick={() => setCategoryPage((prev) => Math.min(Math.ceil(dbCategories.length / itemsPerPage) - 1, prev + 1))}
                            disabled={categoryPage === Math.ceil(dbCategories.length / itemsPerPage) - 1}
                          >
                            {t("editBot.steps.basic.pagination.next")}
                          </button>
                        </div>
                      )}
                    </div>
                    {errors.categories && <span className="error-message">{errors.categories}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      {t("editBot.steps.basic.tags", {
                        count: botData.tags.length,
                      })}
                    </label>
                    <div className="tags-pagination-container">
                      <div className="tags-grid">
                        {dbTags && dbTags.length > 0 ? (
                          dbTags.slice(tagPage * itemsPerPage, (tagPage + 1) * itemsPerPage).map((tag, index) => (
                            <button key={tag.id || index} type="button" className={`tag-button ${botData.tags.includes(tag.id) ? "active" : ""}`} onClick={() => toggleTag(tag.id)}>
                              {tag.name}
                              {botData.tags.includes(tag.id) && <span className="check-badge">✓</span>}
                            </button>
                          ))
                        ) : (
                          <p>Carregando tags...</p>
                        )}
                      </div>
                      {dbTags.length > itemsPerPage && (
                        <div className="pagination-controls">
                          <button type="button" className="pagination-btn" onClick={() => setTagPage((prev) => Math.max(0, prev - 1))} disabled={tagPage === 0}>
                            {t("editBot.steps.basic.pagination.prev")}
                          </button>
                          <span className="pagination-info">
                            {t("editBot.steps.basic.pagination.info", {
                              current: tagPage + 1,
                              total: Math.ceil(dbTags.length / itemsPerPage),
                            })}
                          </span>
                          <button
                            type="button"
                            className="pagination-btn"
                            onClick={() => setTagPage((prev) => Math.min(Math.ceil(dbTags.length / itemsPerPage) - 1, prev + 1))}
                            disabled={tagPage === Math.ceil(dbTags.length / itemsPerPage) - 1}
                          >
                            {t("editBot.steps.basic.pagination.next")}
                          </button>
                        </div>
                      )}
                    </div>
                    {errors.tags && <span className="error-message">{errors.tags}</span>}
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn btn-danger" onClick={() => navigate("/profile")}>
                      {t("editBot.common.cancel")}
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleNext}>
                      {t("editBot.steps.basic.next")}
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="form-step animation-fade-in">
                  <h2>{t("editBot.steps.commands.title")}</h2>
                  <p className="step-description">{t("editBot.steps.commands.description")}</p>

                  <div className="commands-container">
                    {botData.commands.map((command, index) => (
                      <div key={index} className="command-card">
                        <div className="command-header">
                          <h3>
                            {t("editBot.steps.commands.card.title", {
                              index: index + 1,
                            })}
                          </h3>
                          <button type="button" className="btn-remove-command" onClick={() => removeCommand(index)}>
                            ×
                          </button>
                        </div>
                        <div className="command-fields">
                          <div className="form-grid">
                            <div className="form-group">
                              <label>{t("editBot.steps.commands.card.name")}</label>
                              <input
                                type="text"
                                value={command.name}
                                onChange={(e) => updateCommand(index, "name", e.target.value)}
                                placeholder={t("editBot.steps.commands.card.namePlaceholder")}
                                className={errors[`cmd_${index}_name`] ? "error" : ""}
                              />
                              {errors[`cmd_${index}_name`] && <span className="error-message">{errors[`cmd_${index}_name`]}</span>}
                            </div>
                            <div className="form-group">
                              <label>{t("editBot.steps.commands.card.category")}</label>
                              <select value={command.category} onChange={(e) => updateCommand(index, "category", e.target.value)}>
                                {["Geral", "Moderação", "Música", "Diversão", "Utilidade", "Economia", "Configuração"].map((c) => (
                                  <option key={c} value={c}>
                                    {t(`addBot.step3.categories.${c}`)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="form-group">
                            <label>{t("editBot.steps.commands.card.usage")}</label>
                            <input
                              type="text"
                              value={command.usage}
                              onChange={(e) => updateCommand(index, "usage", e.target.value)}
                              placeholder={t("editBot.steps.commands.card.usagePlaceholder")}
                              className={errors[`cmd_${index}_usage`] ? "error" : ""}
                            />
                            {errors[`cmd_${index}_usage`] && <span className="error-message">{errors[`cmd_${index}_usage`]}</span>}
                          </div>
                          <div className="form-group">
                            <label>{t("editBot.steps.commands.card.description")}</label>
                            <textarea
                              value={command.description}
                              onChange={(e) => updateCommand(index, "description", e.target.value)}
                              placeholder={t("editBot.steps.commands.card.descriptionPlaceholder")}
                              rows={2}
                              className={errors[`cmd_${index}_desc`] ? "error" : ""}
                            />
                            {errors[`cmd_${index}_desc`] && <span className="error-message">{errors[`cmd_${index}_desc`]}</span>}
                          </div>
                        </div>
                      </div>
                    ))}

                    {botData.commands.length < 10 && (
                      <button type="button" className="btn-add-command" onClick={addCommand}>
                        {t("editBot.steps.commands.add")}
                      </button>
                    )}
                    {errors.commands && (
                      <p className="error-message" style={{ textAlign: "center" }}>
                        {errors.commands}
                      </p>
                    )}
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={handleBack} disabled={saving}>
                      {t("editBot.steps.commands.back")}
                    </button>
                    {botStatus === "rejected" || isCustomizationEnabled ? (
                      <button type="button" className="btn btn-primary" onClick={handleNext} disabled={saving}>
                        {isCustomizationEnabled ? t("editBot.steps.basic.nextBusiness") : t("editBot.steps.commands.nextReanalysis")}
                      </button>
                    ) : (
                      <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? t("editBot.steps.commands.saving") : t("editBot.steps.commands.save")}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Etapa 3: Customização (Business ou Partner) */}
              {currentStep === 3 && isCustomizationEnabled && (
                <div className="form-step animation-fade-in">
                  <h2>{t("editBot.steps.whiteLabel.title")}</h2>
                  <p
                    className="step-description"
                    dangerouslySetInnerHTML={{
                      __html: t("editBot.steps.whiteLabel.description"),
                    }}
                  ></p>

                  {/* Campo de Vanity URL - Exclusivo para Business */}
                  {botData.apiTier === "business" && (
                    <div className="vanity-url-section mb-5">
                      <div className="form-group">
                        <label htmlFor="vanityUrl">
                          🔗 {t("editBot.steps.whiteLabel.vanityUrlTitle")}
                          <span className="badge bg-primary ms-2">Business Edition</span>
                        </label>
                        <p className="text-muted small mb-3">{t("editBot.steps.whiteLabel.vanityUrlDesc")}</p>
                        <div className="vanity-input-wrapper">
                          <span className="domain-prefix">https://www.botgate.com.br/</span>
                          <input
                            type="text"
                            id="vanityUrl"
                            name="vanityUrl"
                            value={botData.vanityUrl}
                            onChange={(e) => {
                              const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                              setBotData((prev) => ({
                                ...prev,
                                vanityUrl: val,
                              }));
                            }}
                            placeholder={t("editBot.steps.whiteLabel.vanityUrlPlaceholder")}
                            maxLength={50}
                            className="vanity-input"
                          />
                        </div>
                        <div className="vanity-preview mt-2">
                          <span className="text-muted">{t("editBot.steps.whiteLabel.preview")} </span>
                          <code className="vanity-link">
                            https://www.botgate.com.br/
                            {botData.vanityUrl || t("editBot.steps.whiteLabel.vanityUrlFallback")}
                          </code>
                        </div>
                      </div>
                    </div>
                  )}

                  <h3 className="mb-4">🎨 {t("editBot.steps.whiteLabel.paletteTitle")}</h3>
                  <div className="palette-grid">
                    {[
                      {
                        id: "midnight",
                        name: t("editBot.steps.whiteLabel.palette_midnight"),
                        colors: ["#6366f1", "#a855f7"],
                        bg: "#020617",
                      },
                      {
                        id: "sunset",
                        name: t("editBot.steps.whiteLabel.palette_sunset"),
                        colors: ["#f59e0b", "#ef4444"],
                        bg: "#0c0a09",
                      },
                      {
                        id: "neon",
                        name: t("editBot.steps.whiteLabel.palette_neon"),
                        colors: ["#10b981", "#3b82f6"],
                        bg: "#09090b",
                      },
                      {
                        id: "royal",
                        name: t("editBot.steps.whiteLabel.palette_royal"),
                        colors: ["#e11d48", "#4f46e5"],
                        bg: "#020617",
                      },
                      {
                        id: "cyber",
                        name: t("editBot.steps.whiteLabel.palette_cyber"),
                        colors: ["#00f2ff", "#ff00e5"],
                        bg: "#050505",
                      },
                    ].map((p) => (
                      <div
                        key={p.id}
                        className={`palette-card ${botData.whiteLabelSettings.palette === p.id ? "active" : ""}`}
                        onClick={() =>
                          setBotData((prev) => ({
                            ...prev,
                            whiteLabelSettings: {
                              ...prev.whiteLabelSettings,
                              palette: p.id,
                            },
                          }))
                        }
                      >
                        <div className="palette-preview" style={{ background: p.bg }}>
                          <div className="color-dots">
                            {p.colors.map((c) => (
                              <div key={c} style={{ background: c }}></div>
                            ))}
                          </div>
                        </div>
                        <div className="palette-info">
                          <span>{p.name}</span>
                          {botData.whiteLabelSettings.palette === p.id && <span className="selected-check">✓</span>}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={handleBack} disabled={saving}>
                      {t("editBot.steps.commands.back")}
                    </button>
                    {botStatus === "rejected" ? (
                      <button type="button" className="btn btn-primary" onClick={handleNext} disabled={saving}>
                        {t("editBot.steps.commands.next")}
                      </button>
                    ) : (
                      <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? t("editBot.steps.commands.saving") : t("editBot.steps.commands.save")}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Etapa 4: Informações para Reanálise (apenas para bots rejeitados) */}
              {currentStep === 4 && botStatus === "rejected" && (
                <div className="form-step animation-fade-in">
                  <h2>{t("editBot.steps.reanalysis.title")}</h2>
                  <p className="step-description">{t("editBot.steps.reanalysis.description")}</p>

                  <div className="form-group">
                    <label htmlFor="reviewNotes">
                      {t("editBot.steps.reanalysis.notes")}
                      <span className="char-count">{botData.reviewNotes.length}/500</span>
                    </label>
                    <textarea
                      id="reviewNotes"
                      name="reviewNotes"
                      value={botData.reviewNotes}
                      onChange={handleInputChange}
                      rows={6}
                      maxLength={500}
                      placeholder={t("editBot.steps.reanalysis.notesPlaceholder")}
                      className={errors.reviewNotes ? "error" : ""}
                    />
                    {errors.reviewNotes && <span className="error-message">{errors.reviewNotes}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="testInstructions">
                      {t("editBot.steps.reanalysis.test")}
                      <span className="char-count">{botData.testInstructions.length}/500</span>
                    </label>
                    <textarea
                      id="testInstructions"
                      name="testInstructions"
                      value={botData.testInstructions}
                      onChange={handleInputChange}
                      rows={6}
                      maxLength={500}
                      placeholder={t("editBot.steps.reanalysis.testPlaceholder")}
                      className={errors.testInstructions ? "error" : ""}
                    />
                    {errors.testInstructions && <span className="error-message">{errors.testInstructions}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="additionalInfo">
                      {t("editBot.steps.reanalysis.additional")}
                      <span className="char-count">{botData.additionalInfo.length}/500</span>
                    </label>
                    <textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      value={botData.additionalInfo}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      placeholder={t("editBot.steps.reanalysis.additionalPlaceholder")}
                    />
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={handleBack} disabled={saving}>
                      {t("editBot.steps.commands.back")}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? t("editBot.steps.commands.saving") : t("editBot.steps.reanalysis.submit")}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="modal-overlay">
            <div className="modal-content success-modal animation-pop-in">
              <div className="success-icon-wrapper">
                <div className="success-checkmark">✓</div>
              </div>
              <h2>{t("editBot.modal.title")}</h2>
              <p>{t("editBot.modal.description")}</p>
              <button className="btn btn-primary" onClick={() => navigate("/profile")}>
                {t("editBot.modal.button")}
              </button>
            </div>
          </div>
        )}
        <StatusModal isOpen={modalOpen} onClose={() => setModalOpen(false)} type={modalConfig.type} title={modalConfig.title} message={modalConfig.message} />
      </div>
    </div>
  );
}

export default EditBotPage;
